import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

import ProductImage from '../ProductImage';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';

/**
 * Horizontal reel of the last-N products the customer opened in the
 * quick-view modal during the current session. Only mounts when the
 * history has at least one item so first-time visitors see a clean
 * catalog. Clicking a tile re-opens the same modal via `onOpen(sku)`.
 */
export const RecentlyViewedReel = ({ onOpen }) => {
    const { t } = useTranslation('portal');
    const { items } = useRecentlyViewed();

    if (!items || items.length === 0) return null;

    return (
        <section data-testid="recently-viewed-reel" className="space-y-3 min-w-0">
            <h3 className="text-[11px] uppercase tracking-[2px] text-gray-500 font-semibold flex items-center gap-1.5">
                <Clock size={12} className="text-[#C08478]" />
                {t('catalog.recently_viewed.title', { defaultValue: 'Recently viewed' })}
            </h3>

            <div className="-mx-2 px-2 overflow-x-auto snap-x snap-mandatory scrollbar-thin">
                <div className="flex gap-3 pb-2">
                    {items.map((p) => (
                        <button
                            key={p.sku}
                            data-testid={`recently-viewed-${p.sku}`}
                            onClick={() => onOpen && onOpen(p.sku)}
                            className="snap-start flex-shrink-0 w-[38%] xs:w-[30%] sm:w-[160px] md:w-[170px] text-left group bg-white border border-[#EFE5E1] rounded-lg overflow-hidden hover:shadow-md hover:border-[#C08478]/40 transition-all focus:outline-none focus:ring-2 focus:ring-[#C08478]/40"
                        >
                            <div className="aspect-square bg-gradient-to-br from-[#FBF8F6] to-[#F5EEEA] overflow-hidden">
                                <ProductImage
                                    product={p}
                                    imgClassName="group-hover:scale-[1.05] transition-transform duration-500"
                                    testid={`recently-viewed-${p.sku}-img`}
                                />
                            </div>
                            <div className="p-2 space-y-0.5">
                                {p.brand && (
                                    <div className="text-[9px] uppercase tracking-[2px] text-[#C08478] font-semibold truncate">
                                        {p.brand}
                                    </div>
                                )}
                                <div className="text-[12px] text-[#1A1A1A] font-medium leading-snug line-clamp-2 min-h-[32px]">
                                    {p.name}
                                </div>
                                {!p.price_hidden && (
                                    <div className="text-[13px] font-bold text-[#1A1A1A] tabular-nums leading-none pt-1">
                                        €{Number(p.price).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default RecentlyViewedReel;
